import { LEVEL, SkillTAB, SubTab, SubType, TAB } from '~/discovery/types/enums';
import type { SkillsTabMap } from './types/skills';

export const PAGE_SIZE = 12;

export const subtypeMap: Record<SubType, { name: string; childName: string }> = {
  [SubType.BOOK]: {
    name: 'book',
    childName: 'chapter',
  },
  [SubType.POEM]: {
    name: 'poem',
    childName: 'verse',
  },
  [SubType.PLAY]: {
    name: 'play',
    childName: 'act',
  },
  [SubType.SHORT_STORY]: {
    name: 'short story',
    childName: 'chapter',
  },
};

type SUB_TAB_CONFIG = Record<SubTab, { key: SubTab; name: string }>;

export const SubTabConfig: SUB_TAB_CONFIG = {
  [SubTab.INTRODUCTION]: {
    key: SubTab.INTRODUCTION,
    name: 'introduction',
  },
  [SubTab.ANTICIPATION_GUIDE]: {
    key: SubTab.ANTICIPATION_GUIDE,
    name: 'anticipation guide',
  },
  [SubTab.CHAPTER_WISE]: {
    key: SubTab.CHAPTER_WISE,
    name: 'chapter wise',
  },
  [SubTab.ACROSS_CHAPTERS]: {
    key: SubTab.ACROSS_CHAPTERS,
    name: 'across chapters',
  },
  [SubTab.ACT_WISE]: {
    key: SubTab.CHAPTER_WISE,
    name: 'acts',
  },
  [SubTab.ACROSS_ACTS]: {
    key: SubTab.ACROSS_CHAPTERS,
    name: 'across acts',
  },
  [SubTab.VERSE_WISE]: {
    key: SubTab.CHAPTER_WISE,
    name: 'verses',
  },
  [SubTab.ACROSS_VERSES]: {
    key: SubTab.ACROSS_CHAPTERS,
    name: 'across verses',
  },
  [SubTab.THEMES]: {
    key: SubTab.THEMES,
    name: 'themes',
  },
  [SubTab.FIGURATIVE_LANGUAGE]: {
    key: SubTab.FIGURATIVE_LANGUAGE,
    name: 'figurative language',
  },
  [SubTab.CHARACTERS]: {
    key: SubTab.CHARACTERS,
    name: 'characters',
  },
  [SubTab.QUOTES]: {
    key: SubTab.QUOTES,
    name: 'quotes',
  },
  [SubTab.CENTRAL_IDEA]: {
    key: SubTab.CENTRAL_IDEA,
    name: 'central idea',
  },
  [SubTab.TEXTUAL_EVIDENCE]: {
    key: SubTab.TEXTUAL_EVIDENCE,
    name: 'textual evidence',
  },
  [SubTab.SETTING]: {
    key: SubTab.SETTING,
    name: 'setting',
  },
  [SubTab.PLOT]: {
    key: SubTab.PLOT,
    name: 'plot',
  },
  [SubTab.POINT_OF_VIEW]: {
    key: SubTab.POINT_OF_VIEW,
    name: 'point of view',
  },
  [SubTab.GENRE]: {
    key: SubTab.GENRE,
    name: 'genre',
  },
  [SubTab.BASIC_INFORMATION]: {
    key: SubTab.BASIC_INFORMATION,
    name: 'basic information',
  },
  [SubTab.TEXT_STRUCTURE]: {
    key: SubTab.TEXT_STRUCTURE,
    name: 'text structure',
  },
  [SubTab.MULTIMEDIA]: {
    key: SubTab.MULTIMEDIA,
    name: 'multimedia',
  },
};

export const TabConfig = {
  [TAB.CHAPTER_REVIEW]: {
    key: TAB.REVIEW,
    name: 'chapter review',
    child: subtypeMap[SubType.BOOK].childName,
  },
  [TAB.ACT_REVIEW]: {
    key: TAB.REVIEW,
    name: 'act review',
    child: subtypeMap[SubType.PLAY].childName,
  },
  [TAB.REVIEW]: {
    key: TAB.REVIEW,
    name: 'review',
  },
  [TAB.PRE_READING]: {
    key: TAB.PRE_READING,
    name: 'pre-reading',
    sub: {
      [SubTab.INTRODUCTION]: SubTabConfig[SubTab.INTRODUCTION],
      [SubTab.ANTICIPATION_GUIDE]: SubTabConfig[SubTab.ANTICIPATION_GUIDE],
    },
  },
  [TAB.VOCABULARY]: {
    key: TAB.VOCABULARY,
    name: 'vocabulary',
    sub: {
      [SubTab.CHAPTER_WISE]: SubTabConfig[SubTab.CHAPTER_WISE],
      [SubTab.ACROSS_CHAPTERS]: SubTabConfig[SubTab.ACROSS_CHAPTERS],
    },
  },
  [TAB.OTHER_SKILLS]: {
    key: TAB.OTHER_SKILLS,
    name: 'other skills',
    sub: {
      [SubTab.THEMES]: SubTabConfig[SubTab.THEMES],
      [SubTab.FIGURATIVE_LANGUAGE]: SubTabConfig[SubTab.FIGURATIVE_LANGUAGE],
      [SubTab.CHARACTERS]: SubTabConfig[SubTab.CHARACTERS],
      [SubTab.QUOTES]: SubTabConfig[SubTab.QUOTES],
      [SubTab.CENTRAL_IDEA]: SubTabConfig[SubTab.CENTRAL_IDEA],
      [SubTab.TEXTUAL_EVIDENCE]: SubTabConfig[SubTab.TEXTUAL_EVIDENCE],
      [SubTab.SETTING]: SubTabConfig[SubTab.SETTING],
      [SubTab.PLOT]: SubTabConfig[SubTab.PLOT],
      [SubTab.POINT_OF_VIEW]: SubTabConfig[SubTab.POINT_OF_VIEW],
      [SubTab.GENRE]: SubTabConfig[SubTab.GENRE],
      [SubTab.BASIC_INFORMATION]: SubTabConfig[SubTab.BASIC_INFORMATION],
      [SubTab.TEXT_STRUCTURE]: SubTabConfig[SubTab.TEXT_STRUCTURE],
      [SubTab.MULTIMEDIA]: SubTabConfig[SubTab.MULTIMEDIA],
    },
  },
  [TAB.FINAL_REVIEW]: {
    key: TAB.FINAL_REVIEW,
    name: 'final review',
  },
  [TAB.LESSONS]: {
    key: TAB.LESSONS,
    name: 'lessons',
  },
};

export const LevelConfig = {
  [LEVEL.DOMAIN]: {
    key: LEVEL.DOMAIN,
    name: 'domain',
  },
  [LEVEL.TOPIC]: {
    key: LEVEL.TOPIC,
    name: 'topic',
  },
  [LEVEL.SUB_TOPIC]: {
    key: LEVEL.SUB_TOPIC,
    name: 'sub topic',
  },
  [LEVEL.SKILL]: {
    key: LEVEL.SKILL,
    name: 'sub topic',
  },
  [LEVEL.SUB_SKILL]: {
    key: LEVEL.SUB_SKILL,
    name: 'sub topic',
  },
};

export const SkillsTabConfig: SkillsTabMap = {
  [SkillTAB.ALL]: {
    value: SkillTAB.ALL,
    title: 'all',
    image: 'https://cf.quizizz.com/discovery/icons/all.png',
  },
  [SkillTAB.QUIZZES]: {
    value: SkillTAB.QUIZZES,
    title: 'quizzes',
    image: 'https://cf.quizizz.com/discovery/icons/quizzes.png',
  },
  [SkillTAB.LESSONS]: {
    value: SkillTAB.LESSONS,
    title: 'lessons',
    image: 'https://cf.quizizz.com/discovery/icons/lessons.png',
  },
  [SkillTAB.GUIDED_PRACTICE]: {
    value: SkillTAB.GUIDED_PRACTICE,
    title: 'Interactive videos',
    image: 'https://cf.quizizz.com/discovery/icons/videos.png',
  },
  // TODO: can cleanup SKILL_PRACTICE and REVIEW if experiment concludes
  [SkillTAB.REVIEW]: {
    value: SkillTAB.REVIEW,
    title: 'review',
    image: 'https://cf.quizizz.com/image/discovery-check.png',
  },
  [SkillTAB.SKILL_PRACTICE]: {
    value: SkillTAB.SKILL_PRACTICE,
    title: 'skill practice',
    image: 'https://cf.quizizz.com/image/discovery-check.png',
  },
};

export const defaultSkillTabPerLevel = {
  [LEVEL.DOMAIN]: SkillTAB.QUIZZES,
  [LEVEL.TOPIC]: SkillTAB.QUIZZES,
  [LEVEL.SUB_TOPIC]: SkillTAB.QUIZZES,
  [LEVEL.SKILL]: SkillTAB.QUIZZES,
  [LEVEL.SUB_SKILL]: SkillTAB.QUIZZES,
};

export const defaultSkillSupply = {
  tabs: [
    SkillTAB.ALL,
    SkillTAB.QUIZZES,
    SkillTAB.LESSONS,
    SkillTAB.GUIDED_PRACTICE,
  ],
};
